import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  AiProposalNodeDTO,
  AiStructureProposalDTO,
  applyAiStructure,
  generateAiStructure,
} from '../../../services/tutorStructureService';

interface AiStructurePanelProps {
  courseId: number;
  onApplied: () => void;
}

function buildParentFirstOrder(
  allNodes: AiProposalNodeDTO[],
  selectedIds: Set<string>
): AiProposalNodeDTO[] {
  const selected = allNodes.filter((n) => selectedIds.has(n.tempId));
  const result: AiProposalNodeDTO[] = [];
  const added = new Set<string>();

  const add = (node: AiProposalNodeDTO) => {
    if (added.has(node.tempId)) return;
    if (node.parentTempId && !added.has(node.parentTempId)) {
      const parent = selected.find((n) => n.tempId === node.parentTempId);
      if (parent) add(parent);
    }
    result.push(node);
    added.add(node.tempId);
  };

  selected.forEach(add);
  return result;
}

export default function AiStructurePanel({ courseId, onApplied }: AiStructurePanelProps) {
  const [proposal, setProposal] = useState<AiStructureProposalDTO | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generateAiStructure(courseId);
      setProposal(result);
      setSelectedIds(new Set(result.nodes.map((n) => n.tempId)));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to generate AI structure. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (tempId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const selectAll = () => {
    if (proposal) setSelectedIds(new Set(proposal.nodes.map((n) => n.tempId)));
  };

  const deselectAll = () => setSelectedIds(new Set());

  const handleAddSelected = async () => {
    if (!proposal || selectedIds.size === 0) return;
    setApplying(true);
    try {
      const sortedNodes = buildParentFirstOrder(proposal.nodes, selectedIds);
      const result = await applyAiStructure(courseId, { mode: 'additive', nodes: sortedNodes });
      setSuccessMsg(`Added ${result.appliedCount} topic(s) to your draft.`);
      setProposal(null);
      onApplied();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to apply nodes. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleReplace = async () => {
    if (!proposal) return;
    setReplaceDialogOpen(false);
    setApplying(true);
    try {
      const allNodes = buildParentFirstOrder(proposal.nodes, new Set(proposal.nodes.map((n) => n.tempId)));
      const result = await applyAiStructure(courseId, { mode: 'replace', nodes: allNodes });
      setSuccessMsg(`Draft replaced with ${result.appliedCount} AI-generated topic(s).`);
      setProposal(null);
      onApplied();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to replace draft. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleDiscard = () => {
    setProposal(null);
    setSelectedIds(new Set());
    setError(null);
  };

  const renderNodeItem = (node: AiProposalNodeDTO, depth = 0) => {
    const children = proposal?.nodes.filter((n) => n.parentTempId === node.tempId) ?? [];
    return (
      <React.Fragment key={node.tempId}>
        <ListItem disablePadding sx={{ pl: depth * 2 }}>
          <ListItemButton dense onClick={() => toggleSelect(node.tempId)}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox edge="start" checked={selectedIds.has(node.tempId)} tabIndex={-1} disableRipple size="small" />
            </ListItemIcon>
            <ListItemText
              primary={node.title}
              secondary={node.description || undefined}
              primaryTypographyProps={{ variant: 'body2', fontWeight: depth === 0 ? 600 : 400 }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </ListItemButton>
        </ListItem>
        {children.map((child) => renderNodeItem(child, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3 }}>
        <CircularProgress size={28} />
        <Typography variant="caption" color="text.secondary">
          Generating AI structure…
        </Typography>
      </Box>
    );
  }

  if (!proposal) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          AI Structure
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Let AI suggest a topic tree based on this course's subject.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerate}
          fullWidth
        >
          Generate AI structure
        </Button>
      </Box>
    );
  }

  const rootNodes = proposal.nodes.filter((n) => !n.parentTempId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        AI Structure Proposal
      </Typography>

      {proposal.explanation && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {proposal.explanation}
        </Typography>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Button size="small" onClick={selectAll} variant="text" sx={{ fontSize: 11 }}>
          All
        </Button>
        <Button size="small" onClick={deselectAll} variant="text" sx={{ fontSize: 11 }}>
          None
        </Button>
      </Box>

      <List dense disablePadding sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {rootNodes.map((node) => renderNodeItem(node))}
      </List>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Button
          variant="contained"
          size="small"
          disabled={selectedIds.size === 0 || applying}
          onClick={handleAddSelected}
          fullWidth
        >
          {applying ? <CircularProgress size={16} /> : `Add selected (${selectedIds.size})`}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="warning"
          onClick={() => setReplaceDialogOpen(true)}
          disabled={applying}
          fullWidth
        >
          Replace draft
        </Button>
        <Button size="small" onClick={handleDiscard} disabled={applying} fullWidth>
          Discard
        </Button>
        <Button
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerate}
          disabled={applying}
          fullWidth
          variant="text"
        >
          Regenerate
        </Button>
      </Box>

      <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)}>
        <DialogTitle>Replace current draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete your entire current draft and replace it with the AI proposal. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReplace} color="warning" variant="contained">
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
    </Box>
  );
}
